/**
 * FIFO 割当（SPEC §4.4）のクライアント側再現。環境非依存の純関数。
 *
 * 割当そのものはサーバが行い、その結果が正。ここでの計算は
 * 「開封」タップ直後の楽観的 UI（「購入記録なし」バッジの出し分け）に使う。
 * サーバ側 `findFifoPurchaseId`（functions/api/_lib.ts）と同じ順序規則
 * `purchased_at ASC, created_at ASC, id ASC` を守ること。
 */
import type { LotRow, PurchaseRow } from './types';

/** 有効な購入を FIFO 順（古い順）に並べる */
export function sortPurchasesFifo(purchases: PurchaseRow[]): PurchaseRow[] {
  return purchases
    .filter((p) => p.deleted_at === null)
    .slice()
    .sort(
      (a, b) =>
        cmp(a.purchased_at, b.purchased_at) || cmp(a.created_at, b.created_at) || cmp(a.id, b.id),
    );
}

/**
 * itemId に対して次のロットを割り当てるべき purchase の ID。
 * 空きが無ければ null（＝在庫0での開封。SPEC §4.4 で許可されている）。
 */
export function fifoPurchaseId(
  purchases: PurchaseRow[],
  lots: LotRow[],
  itemId: string,
): string | null {
  const allocated = new Map<string, number>();
  for (const lot of lots) {
    if (lot.deleted_at !== null || lot.purchase_id === null) continue;
    allocated.set(lot.purchase_id, (allocated.get(lot.purchase_id) ?? 0) + 1);
  }

  for (const p of sortPurchasesFifo(purchases)) {
    if (p.item_id !== itemId) continue;
    if (p.qty > (allocated.get(p.id) ?? 0)) return p.id;
  }
  return null;
}

/** 未開封在庫数 = Σ有効purchases.qty − 有効lots数（SPEC §4.3） */
export function unopenedStock(purchases: PurchaseRow[], lots: LotRow[], itemId: string): number {
  let qty = 0;
  for (const p of purchases) {
    if (p.deleted_at === null && p.item_id === itemId) qty += p.qty;
  }
  let opened = 0;
  for (const l of lots) {
    if (l.deleted_at === null && l.item_id === itemId) opened += 1;
  }
  return qty - opened;
}

const cmp = (a: string, b: string): number => (a < b ? -1 : a > b ? 1 : 0);
